import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useLang } from "@/hooks/use-language";
import { REWARDS } from "@/lib/content";
import { sounds } from "@/lib/sounds";
import { toast } from "sonner";
import { ArrowLeft, Star, Lock } from "lucide-react";
import { redeemReward, listRewardRedemptions } from "@/lib/content.functions";
import { useSmartBack } from "@/hooks/use-smart-back";

export const Route = createFileRoute("/_authenticated/app/child/$childId/rewards")({
  component: Rewards,
});

function Rewards() {
  const { childId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack(() => navigate({ to: "/app/child/$childId", params: { childId } }));
  const { lang, t } = useLang();
  const [points, setPoints] = useState<number>(0);
  const [redeemed, setRedeemed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  const redeemFn = useServerFn(redeemReward);
  const listFn = useServerFn(listRewardRedemptions);

  useEffect(() => {
    supabase
      .from("children")
      .select("total_points")
      .eq("id", childId)
      .single()
      .then(({ data }) => data && setPoints(data.total_points));
    listFn({ data: { childId } })
      .then((rows) => setRedeemed(new Set((rows ?? []).map((r: any) => r.reward_id))))
      .catch(() => {});
  }, [childId, listFn]);

  const redeem = async (id: string, cost: number, label: string) => {
    if (busy) return;
    if (points < cost) {
      toast.error(t("notEnough"));
      return;
    }
    setBusy(id);
    try {
      const res = await redeemFn({ data: { childId, rewardId: id, cost } });
      if (!res.ok) {
        toast.error(res.error ?? (lang === "ar" ? "تعذّر الاستبدال" : "Could not redeem"));
        return;
      }
      // Reward redeem = cozy milestone → settle chord (rate-limited in controller).
      sounds.settle();
      setRedeemed((prev) => new Set([...prev, id]));
      setPoints(res.remaining ?? Math.max(0, points - cost));
      const { Events } = await import("@/lib/analytics");
      Events.rewardRedeemed(id, cost);
      toast.success(`🎉 ${label}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="px-5 py-6 max-w-2xl mx-auto">
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-sm font-bold text-foreground/60 mb-4 hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> {t("backToHome")}
      </button>

      <div className="bg-gradient-to-br from-primary/15 to-accent/10 rounded-3xl p-6 mb-6 text-center">
        <p className="text-xs uppercase tracking-widest font-bold text-foreground/60 mb-1">
          {t("points")}
        </p>
        <p className="font-display-en text-5xl font-black text-primary">⭐ {points}</p>
      </div>

      <h1 className="font-display-en text-2xl font-bold tracking-tight mb-4">{t("shop")}</h1>

      <div className="grid grid-cols-2 gap-3">
        {REWARDS.map((r) => {
          const owned = redeemed.has(r.id);
          const can = points >= r.cost;
          return (
            <button
              key={r.id}
              onClick={() => !owned && redeem(r.id, r.cost, lang === "ar" ? r.ar : r.en)}
              disabled={owned}
              className={`bg-card border-2 rounded-3xl p-5 text-center transition-all ${
                owned
                  ? "border-secondary/60 bg-secondary/5"
                  : can
                    ? "border-clay/30 hover:border-primary hover:-translate-y-1"
                    : "border-clay/30 opacity-60"
              }`}
            >
              <div className="text-5xl mb-2">{r.emoji}</div>
              <p className="font-display-ar text-base text-primary mb-0.5" dir="rtl">
                {r.ar}
              </p>
              <h3 className="font-bold text-sm mb-2">{r.en}</h3>
              {owned ? (
                <span className="text-xs font-bold text-secondary">✓ {t("completed")}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-sm font-black text-primary">
                  {!can && <Lock className="size-3" />}
                  <Star className="size-3" /> {r.cost}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
